import { createContext, type PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { type StoreApi } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { useRegisterOcBetslipCallbacks } from '@cx-modules/hooks';
import { NoContent } from '@cx-modules/ui';

import { useQuery } from '../hooks/useQuery';
import { MissingRegionContextError } from '../hooks/useRegionValidation';
import { createOddsGridStore, type Store } from '../OddsGrid.store';
import { type OddsGridProps, type OddsGridStoreProps, type SyncBet } from '../types';

const UserFacingErrorFallback = ({ error }: { error: Error }) => {
  if (error instanceof MissingRegionContextError) {
    return <NoContent>{error.message}</NoContent>;
  }

  throw error;
};

export const StoreContext = createContext<StoreApi<Store> | null>(null);

const StoreProviderInner = ({ children, ...oddsGridProps }: PropsWithChildren<OddsGridProps>) => {
  const { data } = useQuery(oddsGridProps);

  const {
    autoContrastOfferTooltips,
    betIdsToShow,
    categoryGroupId,
    disableStreamer,
    firstBetOnMobileStartsExpanded,
    marketName,
    oddsFormat,
    oddsShown,
    onOddsButtonClick,
    onOfferClick,
    repub,
    resourcesUrl,
    showBookieLogo,
    tooltipForNewUser,
  } = oddsGridProps;

  const storeProps: OddsGridStoreProps = useMemo(
    () => ({
      autoContrastOfferTooltips,
      betIdsToShow,
      categoryGroupId,
      disableStreamer,
      firstBetOnMobileStartsExpanded,
      marketName,
      oddsFormat,
      oddsShown,
      onOddsButtonClick,
      onOfferClick,
      repub,
      resourcesUrl,
      showBookieLogo,
      tooltipForNewUser,
    }),
    [
      autoContrastOfferTooltips,
      betIdsToShow,
      categoryGroupId,
      disableStreamer,
      firstBetOnMobileStartsExpanded,
      marketName,
      oddsFormat,
      oddsShown,
      onOddsButtonClick,
      onOfferClick,
      repub,
      resourcesUrl,
      showBookieLogo,
      tooltipForNewUser,
    ],
  );

  const store = useMemo(() => createOddsGridStore({ data, ...storeProps }), [data, storeProps]);

  const syncBetslip = useCallback(
    (bets: SyncBet[], overwriteSelections?: boolean) => {
      store.getState().syncBetslip?.(bets, overwriteSelections);
    },
    [store],
  );

  const removeBet = useCallback(
    (betIdOrObj: number | { betId: number }) => {
      const betId = typeof betIdOrObj === 'number' ? betIdOrObj : betIdOrObj.betId;
      store.getState().removeSelectedBet?.(betId);
    },
    [store],
  );

  const removeAllBets = useCallback(() => {
    store.getState().removeAllSelectedBets?.();
  }, [store]);

  useRegisterOcBetslipCallbacks({ clearBetslip: removeAllBets, removeAllBets, removeBet, syncBetslip }, [
    removeAllBets,
    removeBet,
    syncBetslip,
  ]);

  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
};

export const StoreProvider = ({ children, ...oddsGridProps }: PropsWithChildren<OddsGridProps>) => {
  return (
    <ErrorBoundary
      FallbackComponent={UserFacingErrorFallback}
      onError={error => {
        if (!(error instanceof MissingRegionContextError)) {
          console.error('OddsGrid Error:', error);
        }
      }}
    >
      <StoreProviderInner {...oddsGridProps}>{children}</StoreProviderInner>
    </ErrorBoundary>
  );
};

const defaultEqualityFn = <T,>(a: T, b: T) => Object.is(a, b);

export function useSelector<T>(selector: (state: Store) => T, equalityFn?: (a: T, b: T) => boolean): T | never {
  const store = useContext(StoreContext);
  if (!store) throw new Error('Missing StoreProvider in the tree');

  return useStoreWithEqualityFn(store, selector, equalityFn ?? defaultEqualityFn);
}

export { shallow };
